import {
  COMMON_WORDS,
  EXTRA_WORDS,
  PROPER_WORDS,
} from "@/lib/tool-kit/style-checkers/spell-check-data";
import { spellingPairs } from "@/lib/tool-kit/text-analysis/spelling-data";
import {
  isSentenceStart,
  type Span,
} from "@/lib/tool-kit/writing-analysis/text";

/**
 * A dictionary-based spelling check that runs in the browser.
 *
 * The dictionary is ENABLE (a public-domain Scrabble-style list of about 168,000
 * lowercase words, fetched once from this site's `public/data`) plus a short list of
 * contractions, abbreviations, modern vocabulary and proper nouns that ENABLE lacks.
 *
 * What it can find: strings that are not words. What it cannot find: a real word used
 * for the wrong one (their/there, form/from), a missing word, or a name it has never
 * heard of. ENABLE accepts both British and American spellings (colour and color,
 * organise and organize), so a region option flags the other variety separately, using
 * the same pairs as the site's British–American converter.
 *
 * Suggestions are the dictionary words nearest to the misspelling by an edit distance
 * that charges less for the mistakes typists actually make: swapped neighbours
 * (teh → the) and a key hit next to the intended one (wprd → word).
 */

export interface Dictionary {
  words: ReadonlySet<string>;
  byLength: ReadonlyMap<number, readonly string[]>;
}

export function createDictionary(list: readonly string[]): Dictionary {
  const words = new Set(list);
  const byLength = new Map<number, string[]>();
  for (const word of words) {
    const group = byLength.get(word.length);
    if (group) {
      group.push(word);
    } else {
      byLength.set(word.length, [word]);
    }
  }
  return { words, byLength };
}

/* ------------------------------------------------------------ keyboard */

const KEY_ROWS = ["qwertyuiop", "asdfghjkl", "zxcvbnm"] as const;

const NEIGHBOURS: ReadonlyMap<string, ReadonlySet<string>> = (() => {
  const map = new Map<string, Set<string>>();
  const add = (a: string | undefined, b: string | undefined) => {
    if (a && b) {
      map.get(a)?.add(b);
      map.get(b)?.add(a);
    }
  };
  for (const row of KEY_ROWS) {
    for (const key of row) {
      map.set(key, new Set());
    }
  }
  for (const [r, row] of KEY_ROWS.entries()) {
    for (let c = 0; c < row.length; c += 1) {
      add(row[c], row[c + 1]);
      const below = KEY_ROWS[r + 1];
      if (below) {
        add(row[c], below[c - 1]);
        add(row[c], below[c]);
      }
    }
  }
  return map;
})();

/** Two different keys that touch on a QWERTY keyboard. */
export function adjacentKeys(a: string, b: string): boolean {
  return NEIGHBOURS.get(a)?.has(b) ?? false;
}

/** A letter left out of the typed word. Omissions are the commonest typing slip. */
const OMITTED = 0.9;
/** A letter typed that should not be there. */
const EXTRA = 1;
/** Leaving out or doubling a letter that is already doubled-or-adjacent: "comming", "begining". */
const DOUBLING = 0.8;
const SUB_ADJACENT = 0.7;
const SUB = 1;
const TRANSPOSE = 0.6;

function substitution(a: string, b: string): number {
  if (a === b) {
    return 0;
  }
  return adjacentKeys(a, b) ? SUB_ADJACENT : SUB;
}

/**
 * Damerau–Levenshtein distance (optimal string alignment) with keyboard weights:
 * a letter left out of `a` 0.9 and a letter typed that `b` lacks 1 (0.8 each when
 * the letter is a doubled one: "comming", "begining"), a swap of two neighbouring letters 0.6, a substitution
 * 0.7 when the keys touch and 1 otherwise. Returns `Infinity` as soon as it is
 * certain the distance exceeds `limit`.
 */
export function weightedDistance(
  a: string,
  b: string,
  limit = Number.POSITIVE_INFINITY
): number {
  const n = a.length;
  const m = b.length;
  if (Math.abs(n - m) * OMITTED > limit) {
    return Number.POSITIVE_INFINITY;
  }
  let beforePrevious = new Array<number>(m + 1).fill(0);
  let previous = Array.from({ length: m + 1 }, (_, j) => j * OMITTED);
  let current = new Array<number>(m + 1).fill(0);
  let previousMin = 0;
  for (let i = 1; i <= n; i += 1) {
    current[0] = i * EXTRA;
    let rowMin = current[0];
    for (let j = 1; j <= m; j += 1) {
      const doubledInA = a[i - 1] === a[i - 2] || a[i - 1] === a[i];
      const doubledInB = b[j - 1] === b[j - 2] || b[j - 1] === b[j];
      let cost = Math.min(
        previous[j] + (doubledInA ? DOUBLING : EXTRA),
        current[j - 1] + (doubledInB ? DOUBLING : OMITTED),
        previous[j - 1] + substitution(a[i - 1], b[j - 1])
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        cost = Math.min(cost, beforePrevious[j - 2] + TRANSPOSE);
      }
      current[j] = cost;
      rowMin = Math.min(rowMin, cost);
    }
    if (Math.min(rowMin, previousMin) > limit) {
      return Number.POSITIVE_INFINITY;
    }
    previousMin = rowMin;
    [beforePrevious, previous, current] = [previous, current, beforePrevious];
  }
  return previous[m];
}

/* ------------------------------------------------------------ suggestions */

const FIRST_LETTER_PENALTY = 0.25;
const COMMON_BONUS = 0.3;
const MAX_LENGTH_GAP = 2;
/** Cost of about one slip: the first pass of a search looks no further. */
const NEAR_MISS = 1.0;

function limitFor(length: number): number {
  return length <= 4 ? 1.3 : 2.2;
}

const RE_NON_LETTERS = /[^\p{L}]/gu;
const RE_DIACRITICS = /\p{M}/gu;
const RE_ALL_UPPER = /^\p{Lu}+$/u;
const RE_CAPITALISED = /^\p{Lu}\p{Ll}/u;

/** The suggestion in the case the misspelling was typed in. */
export function matchCase(original: string, suggestion: string): string {
  if (original.length > 1 && RE_ALL_UPPER.test(original)) {
    return suggestion.toUpperCase();
  }
  if (RE_CAPITALISED.test(original)) {
    return suggestion.charAt(0).toUpperCase() + suggestion.slice(1);
  }
  return suggestion;
}

function stripMarks(word: string): string {
  return word.normalize("NFD").replace(RE_DIACRITICS, "");
}

/** Cheapest conceivable cost of one unit of letter-count difference (half a keyboard substitution). */
const MIN_COST_PER_LETTER = 0.35;
const OTHER_BUCKET = 26;

function bucket(code: number): number {
  const slot = code - 97;
  return slot >= 0 && slot < 26 ? slot : OTHER_BUCKET;
}

/**
 * How many letters the two words differ by, as multisets. Every edit changes this by at
 * most 2 and costs at least 0.7, so a large difference rules a candidate out before the
 * (much slower) alignment is attempted.
 */
function makeBagDifference(target: string): (candidate: string) => number {
  const base = new Int8Array(OTHER_BUCKET + 1);
  for (let i = 0; i < target.length; i += 1) {
    base[bucket(target.charCodeAt(i))] += 1;
  }
  const work = new Int8Array(OTHER_BUCKET + 1);
  return (candidate) => {
    work.set(base);
    let matched = 0;
    for (let i = 0; i < candidate.length; i += 1) {
      const slot = bucket(candidate.charCodeAt(i));
      if (work[slot] > 0) {
        work[slot] -= 1;
        matched += 1;
      }
    }
    return target.length - matched + (candidate.length - matched);
  };
}

interface Scored {
  word: string;
  score: number;
}

/** Candidates within `limit` of `target`, scored: lower is better. */
function collect(
  target: string,
  proper: boolean,
  dictionary: Dictionary,
  limit: number
): Scored[] {
  const scored: Scored[] = [];
  const bagDifference = makeBagDifference(target);
  const maxBag = Math.floor(limit / MIN_COST_PER_LETTER);
  const consider = (candidate: string) => {
    if (bagDifference(candidate) > maxBag) {
      return;
    }
    const distance = weightedDistance(target, candidate, limit);
    if (distance > limit) {
      return;
    }
    let score = distance;
    if (candidate[0] !== target[0]) {
      score += FIRST_LETTER_PENALTY;
    }
    if (COMMON_WORDS.has(candidate)) {
      score -= COMMON_BONUS;
    }
    scored.push({ word: candidate, score });
  };
  for (
    let length = target.length - MAX_LENGTH_GAP;
    length <= target.length + MAX_LENGTH_GAP;
    length += 1
  ) {
    for (const candidate of dictionary.byLength.get(length) ?? []) {
      consider(candidate);
    }
  }
  for (const candidate of extraCandidates()) {
    if (
      Math.abs(candidate.length - target.length) <= MAX_LENGTH_GAP &&
      (proper || !PROPER_WORDS.has(candidate))
    ) {
      consider(candidate);
    }
  }
  return scored;
}

/**
 * The nearest dictionary words, best first. Ties go to the more common word, then to
 * the one sharing the first letter, then alphabetically. A first pass looks only at
 * near misses (one slip); the wider search runs only if that finds too few.
 */
export function suggest(
  misspelling: string,
  dictionary: Dictionary,
  max = 5
): string[] {
  const target = stripMarks(misspelling.normalize("NFC").toLowerCase()).replace(
    RE_NON_LETTERS,
    ""
  );
  if (target.length < 2) {
    return [];
  }
  const proper = RE_CAPITALISED.test(misspelling);
  const limit = limitFor(target.length);
  let scored = collect(target, proper, dictionary, Math.min(limit, NEAR_MISS));
  const distinct = new Set(scored.map((entry) => entry.word));
  distinct.delete(target);
  if (distinct.size < max) {
    scored = collect(target, proper, dictionary, limit);
  }
  scored.sort((a, b) => a.score - b.score || a.word.localeCompare(b.word));
  const seen = new Set<string>();
  const out: string[] = [];
  for (const { word } of scored) {
    if (seen.has(word) || word === target) {
      continue;
    }
    seen.add(word);
    out.push(matchCase(misspelling, word));
    if (out.length >= max) {
      break;
    }
  }
  return out;
}

let extras: string[] | null = null;

function extraCandidates(): string[] {
  if (!extras) {
    extras = [...EXTRA_WORDS].filter((word) => !word.includes("'"));
  }
  return extras;
}

/* ------------------------------------------------------------ region */

export type Region = "both" | "us" | "uk";

/** Words that are another ordinary word in the other variety: never flagged. */
const NOT_REGIONAL: ReadonlySet<string> = new Set([
  "check",
  "checks",
  "story",
  "stories",
  "draft",
  "drafts",
  "tire",
  "tires",
  "curb",
  "curbs",
  "program",
  "programs",
  "practice",
  "practices",
  "license",
  "licenses",
  "meter",
  "meters",
  "analyses",
]);

interface RegionalMaps {
  /** British spelling → American, for words safe to convert that way. */
  ukToUs: ReadonlyMap<string, string>;
  /** American spelling → British. */
  usToUk: ReadonlyMap<string, string>;
  /** Every spelling in the pairs, either side. */
  all: ReadonlySet<string>;
}

let regional: RegionalMaps | null = null;

function regionalMaps(): RegionalMaps {
  if (regional) {
    return regional;
  }
  const ukToUs = new Map<string, string>();
  const usToUk = new Map<string, string>();
  const all = new Set<string>();
  for (const pair of spellingPairs()) {
    const uk = pair.uk.toLowerCase();
    const us = pair.us.toLowerCase();
    all.add(uk);
    all.add(us);
    if (pair.only !== "us-uk" && !NOT_REGIONAL.has(uk)) {
      ukToUs.set(uk, us);
    }
    if (pair.only !== "uk-us" && !NOT_REGIONAL.has(us)) {
      usToUk.set(us, uk);
    }
  }
  regional = { ukToUs, usToUk, all };
  return regional;
}

/* ------------------------------------------------------------ tokens */

export interface SpellOptions {
  /** Skip capitalised words that do not start a sentence: they are usually names. */
  ignoreCapitalised: boolean;
  /** Skip ALL-CAPS words (APA, NASA) and their plurals (APIs). */
  ignoreAcronyms: boolean;
  region: Region;
  /** Lowercase words the writer chose to accept. */
  ignore: ReadonlySet<string>;
}

export const DEFAULT_SPELL_OPTIONS: SpellOptions = {
  ignoreCapitalised: true,
  ignoreAcronyms: true,
  region: "both",
  ignore: new Set(),
};

export type SpellKind = "misspelling" | "regional";

export interface SpellFinding extends Span {
  /** As written. */
  word: string;
  /** Lowercase, NFC, straight apostrophes: the identity used for grouping and ignoring. */
  key: string;
  kind: SpellKind;
  /** For a regional spelling: the other variety's form, in the word's own case. */
  replacement: string | null;
}

export interface SpellReport {
  /** Words looked at (single letters and skipped items excluded). */
  checked: number;
  findings: SpellFinding[];
}

const RE_SKIP =
  /(?:https?:\/\/|www\.)\S+|[\w.+-]+@[\w-]+(?:\.[\w-]+)+|\b10\.\d{4,9}\/\S+|[#@][\p{L}\p{N}_]+/giu;
const RE_CHUNK =
  /[\p{L}\p{M}\p{N}]+(?:['’][\p{L}\p{M}\p{N}]+|[‐‑-][\p{L}\p{M}\p{N}]+)*/gu;
const RE_HYPHEN_SPLIT = /[‐‑-]/;
const RE_DIGIT = /\p{N}/u;
const RE_CURLY = /’/g;
const RE_INTERNAL_CAPITAL = /\p{Ll}\p{Lu}/u;
const RE_PLURAL_ACRONYM = /^\p{Lu}{2,}s$/u;
const RE_LETTER_OR_DIGIT = /[\p{L}\p{N}]/u;
const RE_AFTER_ABBREVIATION =
  /(?:\b\p{Lu}|\b(?:dr|mr|mrs|ms|prof|st|jr|sr|mt|fig|no|vol|pp|ed|eds|al|vs|cf|ca))\.\s+$/iu;
const RE_POSSESSIVE = /'s$/;
const RE_CLITIC = /'(?:s|d|ll|re|ve|m)$/;

function skipSpans(text: string): Span[] {
  return [...text.matchAll(RE_SKIP)].map((m) => ({
    start: m.index,
    end: m.index + m[0].length,
  }));
}

function overlaps(spans: readonly Span[], start: number, end: number): boolean {
  return spans.some((span) => start < span.end && end > span.start);
}

/** "report.docx": a chunk glued to a letter or digit by a full stop is part of a name. */
function followsDottedName(text: string, start: number): boolean {
  return (
    start > 1 &&
    text[start - 1] === "." &&
    RE_LETTER_OR_DIGIT.test(text[start - 2])
  );
}

export function wordKey(word: string): string {
  return word.normalize("NFC").toLowerCase().replace(RE_CURLY, "'");
}

function known(key: string, dictionary: Dictionary): boolean {
  if (
    dictionary.words.has(key) ||
    EXTRA_WORDS.has(key) ||
    regionalMaps().all.has(key)
  ) {
    return true;
  }
  const bare = stripMarks(key);
  if (bare !== key && (dictionary.words.has(bare) || EXTRA_WORDS.has(bare))) {
    return true;
  }
  if (key.includes("'")) {
    const stem = key.replace(RE_POSSESSIVE, "").replace(RE_CLITIC, "");
    return stem !== key && known(stem, dictionary);
  }
  return false;
}

function exempt(
  word: string,
  key: string,
  atSentenceStart: boolean,
  options: SpellOptions
): boolean {
  if (options.ignore.has(key) || RE_INTERNAL_CAPITAL.test(word)) {
    return true;
  }
  if (
    options.ignoreAcronyms &&
    (RE_ALL_UPPER.test(word) || RE_PLURAL_ACRONYM.test(word))
  ) {
    return true;
  }
  return (
    options.ignoreCapitalised && !atSentenceStart && RE_CAPITALISED.test(word)
  );
}

function regionalReplacement(
  word: string,
  key: string,
  region: Region
): string | null {
  if (region === "both") {
    return null;
  }
  const maps = regionalMaps();
  const target = (region === "us" ? maps.ukToUs : maps.usToUk).get(key);
  return target ? matchCase(word, target) : null;
}

interface Part {
  word: string;
  start: number;
  end: number;
}

function parts(chunk: string, offset: number): Part[] {
  const result: Part[] = [];
  let cursor = 0;
  for (const piece of chunk.split(RE_HYPHEN_SPLIT)) {
    const start = offset + chunk.indexOf(piece, cursor);
    result.push({ word: piece, start, end: start + piece.length });
    cursor = chunk.indexOf(piece, cursor) + piece.length;
  }
  return result;
}

function lettersIn(word: string): number {
  return word.replace(RE_NON_LETTERS, "").length;
}

/** Check `text` against `dictionary`. Offsets index the reader's own text. */
export function checkSpelling(
  text: string,
  dictionary: Dictionary,
  options: SpellOptions = DEFAULT_SPELL_OPTIONS
): SpellReport {
  const skips = skipSpans(text);
  const findings: SpellFinding[] = [];
  let checked = 0;
  for (const match of text.matchAll(RE_CHUNK)) {
    const start = match.index;
    const end = start + match[0].length;
    if (
      RE_DIGIT.test(match[0]) ||
      overlaps(skips, start, end) ||
      followsDottedName(text, start)
    ) {
      continue;
    }
    for (const part of parts(match[0], start)) {
      if (lettersIn(part.word) < 2) {
        continue;
      }
      const key = wordKey(part.word);
      const atStart =
        part.start === start &&
        isSentenceStart(text, start) &&
        !RE_AFTER_ABBREVIATION.test(text.slice(Math.max(0, start - 8), start));
      if (exempt(part.word, key, atStart, options)) {
        continue;
      }
      checked += 1;
      if (known(key, dictionary)) {
        const replacement = regionalReplacement(part.word, key, options.region);
        if (replacement) {
          findings.push({
            start: part.start,
            end: part.end,
            word: part.word,
            key,
            kind: "regional",
            replacement,
          });
        }
        continue;
      }
      findings.push({
        start: part.start,
        end: part.end,
        word: part.word,
        key,
        kind: "misspelling",
        replacement: null,
      });
    }
  }
  return { checked, findings };
}

/* ------------------------------------------------------------ grouping */

export interface UniqueFinding {
  key: string;
  kind: SpellKind;
  /** The first spelling seen, in the case it was typed. */
  word: string;
  replacement: string | null;
  count: number;
  /** Offsets of every occurrence. */
  spans: Span[];
}

/** One entry per distinct word, in order of first appearance. */
export function groupFindings(
  findings: readonly SpellFinding[]
): UniqueFinding[] {
  const groups = new Map<string, UniqueFinding>();
  for (const finding of findings) {
    const id = `${finding.kind}:${finding.key}`;
    const group = groups.get(id);
    const span = { start: finding.start, end: finding.end };
    if (group) {
      group.count += 1;
      group.spans.push(span);
    } else {
      groups.set(id, {
        key: finding.key,
        kind: finding.kind,
        word: finding.word,
        replacement: finding.replacement,
        count: 1,
        spans: [span],
      });
    }
  }
  return [...groups.values()];
}

/** Replace every occurrence in `spans` with `replacement` (kept in each one's own case). */
export function replaceSpans(
  text: string,
  spans: readonly Span[],
  replacement: string
): string {
  let result = text;
  for (const span of [...spans].sort((a, b) => b.start - a.start)) {
    result =
      result.slice(0, span.start) +
      matchCase(result.slice(span.start, span.end), replacement) +
      result.slice(span.end);
  }
  return result;
}

const RE_IGNORE_SPLIT = /[\s,;]+/;

/** "Zotero, Mendeley" → a set of lowercase keys. */
export function parseIgnoreList(raw: string): Set<string> {
  return new Set(
    raw
      .split(RE_IGNORE_SPLIT)
      .map(wordKey)
      .filter((word) => word.length > 0)
  );
}
